/** Several waste piles in one breath, including “no, sorry, it’s actually …”. */

import type { SpokenWalkDraft } from "./spokenWalkPlain";
import { bareSpokenToken, parseSpokenQty } from "./spokenNumberWords";

const SIZE_NEXT = new Set(["by", "x", "light", "lite", "wide"]);
const WASTE_STOP = new Set([
  "that", "thats", "that's", "which", "should", "probably", "prob", "but", "got",
  "marring", "notches", "notch", "because", "so",
]);
const WASTE_LEAD = new Set([
  "i", "see", "seen", "another", "or", "its", "it's", "it", "is", "a", "an", "the",
]);

function qtyAt(tokens: string[], index: number): { value: number; next: number } | null {
  const parsed = parseSpokenQty(tokens.slice(index));
  if (!parsed) return null;
  return { value: parsed.value, next: index + parsed.consumed };
}

function isSizeQty(tokens: string[], index: number, consumed: number): boolean {
  const next = bareSpokenToken(tokens[index + consumed] ?? "");
  if (SIZE_NEXT.has(next)) return true;
  const prev = bareSpokenToken(tokens[index - 1] ?? "");
  return prev === "by" || prev === "x";
}

export function looksLikeWasteMonologue(raw: string): boolean {
  return /\b(another|sorry|unusable|reused|reuse)\b/i.test(raw);
}

function wasteTokens(clause: string): string[] {
  return clause.replace(/[’‘]/g, "'").trim().split(/\s+/).filter((token) => token.length > 0);
}

function wasteCount(tokens: string[]): { value: number; consumed: number } | null {
  let i = 0;
  while (i < tokens.length && WASTE_LEAD.has(bareSpokenToken(tokens[i] ?? ""))) i += 1;
  const qty = qtyAt(tokens, i);
  if (!qty || isSizeQty(tokens, i, qty.next - i)) return null;
  return { value: qty.value, consumed: qty.next };
}

function wasteName(tokens: string[]): string {
  let i = 0;
  while (i < tokens.length && WASTE_LEAD.has(bareSpokenToken(tokens[i] ?? ""))) i += 1;
  const count = wasteCount(tokens);
  if (count) i = count.consumed;
  const kept: string[] = [];
  for (; i < tokens.length; i++) {
    const bare = bareSpokenToken(tokens[i] ?? "");
    if (!bare || WASTE_STOP.has(bare)) break;
    if (WASTE_LEAD.has(bare)) continue;
    kept.push(tokens[i] ?? "");
  }
  return kept
    .join(" ")
    .replace(/\b(?:i\s+believe|up\s+believe|i\s+think)\b/gi, "")
    .replace(/[.,;:]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function wasteClauses(raw: string): string[] {
  return raw
    .replace(/\banother\b/gi, ".\nanother")
    .split(/[.?!]+/)
    .map((clause) => clause.trim())
    .filter((clause) => clause.length > 0);
}

type WasteItem = SpokenWalkDraft & { useAll: boolean };

function applyDisposition(item: WasteItem, text: string) {
  if (/\b(unusable|waste|scrap)\b/i.test(text)) {
    item.secondaryQty = 0;
    item.useAll = false;
    return;
  }
  if (/\b(reused|reuse|reusable|usable)\b/i.test(text)) {
    item.secondaryQty = item.primaryQty;
    item.useAll = true;
  }
}

export function parseWasteMonologue(raw: string): SpokenWalkDraft[] {
  const items: WasteItem[] = [];
  let current: WasteItem | null = null;
  let correcting = false;
  let replaced = false;

  function makeItem(qty: number, name: string, clause: string): WasteItem {
    return {
      primaryQty: qty,
      secondaryQty: null,
      materialName: name,
      raw: clause,
      useAll: false,
    };
  }

  for (const clause of wasteClauses(raw)) {
    if (/^(?:no|nope|sorry|wait)\b/i.test(clause) && clause.split(/\s+/).length <= 4) {
      correcting = true;
      replaced = false;
      continue;
    }
    let text = clause;
    if (/^or\b/i.test(text)) {
      correcting = true;
      replaced = false;
      text = text.replace(/^or\b/i, "").trim();
    }
    const tokens = wasteTokens(text);
    const count = wasteCount(tokens);
    const name = wasteName(tokens);
    const startsAnother = /^another\b/i.test(clause);
    const startNew = !current || startsAnother || (count != null && Boolean(current.materialName) && !correcting);
    if (startNew && (name || startsAnother || count != null)) {
      current = makeItem(count?.value ?? 1, name, clause);
      items.push(current);
      correcting = false;
      replaced = false;
      applyDisposition(current, text);
      continue;
    }
    if (!current) continue;
    if (correcting && name) {
      const newerHasSize = /\bby\b|\dx\d/i.test(name);
      if (!replaced || newerHasSize) {
        current.materialName = name;
        current.raw = `${current.raw} ${clause}`.trim();
        replaced = true;
      }
    }
    applyDisposition(current, text);
    if (current.useAll) current.secondaryQty = current.primaryQty;
  }

  return items.filter((item) => item.materialName.length > 0);
}
